import React, { FC, useEffect, useState } from "react";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { getSectionByID } from "../../Tools/Tools";
import { useAtomValue, useSetAtom } from "jotai";
import { sectionListAtom, selectedSectionAtom, setSelectedSectionAtom } from "../../../../atoms";
import { SectionsList, SelectedSection, SelectedSectionBreadcrumb } from "../SideBar/SideBarInterface";

const LinkBreadcrumb: FC<{
  name?: string;
  id?: string;
  onClick?: () => void;
}> = ({ name = "", id, onClick }) =>
  onClick ? (
    <Link
      underline="hover"
      color="inherit"
      href="#"
      data-section-id={id}
      onClick={(event) => {
        event.preventDefault();
        onClick();
      }}
    >
      {name}
    </Link>
  ) : (
    <span>{name}</span>
  );

interface BreadcrumbItem extends SelectedSectionBreadcrumb {
  ID?: string;
}

// Сначала собираем путь по дереву секций, затем добавляем вкладку и выбранный внутри неё объект.
export const getSectionChain = (
  sections: SectionsList[],
  selectedSection: SelectedSection,
): BreadcrumbItem[] | null => {
  const findChain = (sections: SectionsList[], targetId: string): SectionsList[] | null => {
    return sections.reduce<SectionsList[] | null>((acc, section) => {
      if (acc !== null) return acc;
      if (section.ID === targetId) {
        return [section];
      }
      if (section.Sections) {
        const sub = findChain(section.Sections, targetId);
        if (sub) {
          return [section, ...sub];
        }
      }
      return null;
    }, null);
  };

  const sectionChain = findChain(sections, selectedSection.ID);
  if (!sectionChain) {
    return null;
  }

  // Старый формат Selected.Name оставляем запасным вариантом для секций,
  // которые ещё не заполняют отдельную цепочку Breadcrumbs.
  const selectedName = selectedSection.Selected?.Name || selectedSection.Selected?.name;
  const explicitBreadcrumbs = selectedSection.Breadcrumbs?.filter((item) => item.Name);
  const contextualBreadcrumbs = explicitBreadcrumbs?.length
    ? explicitBreadcrumbs
    : selectedName
      ? [{ Name: selectedName }]
      : [];

  return [...sectionChain, ...contextualBreadcrumbs];
};

//eslint-disable-next-line
const SmetaBreadcrumbs: FC = () => {
  const sideBarSelected = useAtomValue(selectedSectionAtom);
  const sideBarData = useAtomValue(sectionListAtom);
  const [links, setLinks] = useState<BreadcrumbItem[]>([]);
  const setSelectedSection = useSetAtom(setSelectedSectionAtom);

  useEffect(() => {
    if (sideBarSelected.ID !== "" && sideBarData) {
      const chain = getSectionChain(sideBarData, sideBarSelected);
      setLinks(chain || []);
    }
  }, [sideBarData, sideBarSelected.ID, sideBarSelected.Selected, sideBarSelected.Breadcrumbs]);

  const openDocumentSelection = () => {
    setSelectedSection({
      ...sideBarSelected,
      Selected: { Who: "Documents" },
      Breadcrumbs: [{ Name: "Документы" }],
    });
  };

  const onClickLink = (item: BreadcrumbItem) => {
    if (item.ID) {
      if (item.ID === sideBarSelected.ID) {
        openDocumentSelection();
        return;
      }

      const section = getSectionByID(item.ID);
      if (section) setSelectedSection(section);
      return;
    }

    if (item.Name === "Документы") {
      // Who используется как команда для внутренней скрытой вкладки документов:
      // она должна показать список карточек, а не последний открытый документ.
      openDocumentSelection();
    }
  };

  const isClickable = (item: BreadcrumbItem) => Boolean(item.ID || item.Name === "Документы");

  return (
    <Stack spacing={1}>
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        sx={{ color: "inherit" }}
        id={"BreadcrumbsSelected"}
      >
        {links.length > 0 ? (
          links.map((link, index) => (
            <LinkBreadcrumb
              name={link.Name}
              key={`${link.ID || "context"}-${link.Name}-${index}`}
              id={link.ID}
              onClick={isClickable(link) ? () => onClickLink(link) : undefined}
            />
          ))
        ) : (
          <span id="noneBreadcrumbs"></span>
        )}
      </Breadcrumbs>
    </Stack>
  );
};

export default SmetaBreadcrumbs;
